<template>
  <div class="share-page">
    <!-- 页面头部 -->
    <div class="header">
      <el-button type="text" @click="goBack" class="back-btn">
        <i class="el-icon-arrow-left"></i> 返回
      </el-button>
      <h1>分享管理</h1>
      <div class="header-right"></div>
    </div>

    <!-- 加载中 -->
    <div v-if="loading" class="loading-container">
      <el-loading-spinner size="large"></el-loading-spinner>
      <p class="loading-text">加载中...</p>
    </div>

    <!-- 主要内容 -->
    <div v-else class="main-content">
      <!-- 机器人信息 -->
      <el-card v-if="robot" class="robot-info-card">
        <div slot="header">
          <h3>机器人信息</h3>
        </div>
        <div class="robot-details">
          <div class="detail-item">
            <span class="detail-label">名称：</span>
            <span class="detail-value">{{ robot.name }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">设备类型：</span>
            <span class="detail-value">{{ getDeviceTypeLabel(robot.device_type) }}</span>
          </div>
        </div>
      </el-card>

      <!-- 分享列表 -->
      <el-card class="shares-section">
        <div slot="header" class="card-header">
          <h3>分享列表</h3>
          <el-button type="primary" size="small" @click="openCreateShareModal">
            创建分享
          </el-button>
        </div>

        <!-- 加载中 -->
        <div v-if="sharesLoading" class="shares-loading">
          <el-loading-spinner size="medium"></el-loading-spinner>
        </div>

        <!-- 分享列表 -->
        <div v-else-if="shares.length > 0" class="shares-list">
          <div 
            v-for="share in shares" 
            :key="share.id" 
            class="share-item"
            @click="openEditShareModal(share)"
          >
            <div class="share-header">
              <div class="user-info">
                <div class="user-details">
                  <span class="user-name">{{ share.to_user.username }}</span>
                  <span v-if="share.to_user.email" class="user-email">{{ share.to_user.email }}</span>
                </div>
              </div>
              <el-tag 
                :type="share.is_active ? 'success' : 'danger'" 
                size="mini"
              >
                {{ share.is_active ? '启用' : '禁用' }}
              </el-tag>
            </div>

            <div class="share-details">
              <div class="detail-row">
                <span class="detail-label">权限：</span>
                <el-tag 
                  :type="getRoleColor(share.role)" 
                  size="mini"
                >
                  {{ getRoleLabel(share.role) }}
                </el-tag>
              </div>
              <div class="detail-row">
                <span class="detail-label">创建时间：</span>
                <span class="detail-value">{{ share.created_at }}</span>
              </div>
            </div>

            <div class="share-actions">
              <el-button 
                :type="share.is_active ? 'warning' : 'success'"
                size="small"
                @click.stop="toggleShare(share)"
              >
                <i :class="share.is_active ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
                {{ share.is_active ? '禁用' : '启用' }}
              </el-button>
              <el-button 
                type="danger"
                size="small"
                @click.stop="deleteShare(share)"
              >
                <i class="el-icon-delete"></i>
                删除
              </el-button>
            </div>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else class="empty-state">
          <i class="el-icon-share empty-icon"></i>
          <p class="empty-text">暂无分享记录</p>
          <p class="empty-hint">点击右上角按钮创建分享</p>
        </div>
      </el-card>
    </div>

    <!-- 创建分享对话框 -->
    <el-dialog
      title="创建分享"
      :visible.sync="createShareVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="shareForm" :rules="shareFormRules" ref="shareForm" label-width="100px">
        <!-- 搜索用户 -->
        <el-form-item label="分享给用户" prop="identifier">
          <el-input
            v-model="shareForm.identifier"
            placeholder="请输入用户名或邮箱"
            @input="onIdentifierChange"
          >
            <el-button 
              slot="append" 
              @click="handleSearchUser"
              :loading="searchingUser"
            >
              搜索
            </el-button>
          </el-input>
        </el-form-item>

        <!-- 搜索到的用户信息 -->
        <div v-if="foundUser" class="found-user-card">
          <div class="found-user-header">
            <span class="found-user-title">用户信息</span>
          </div>
          <div class="found-user-info">
            <div class="found-details">
              <span class="found-name">{{ foundUser.username }}</span>
              <span v-if="foundUser.email" class="found-email">{{ foundUser.email }}</span>
            </div>
          </div>
        </div>

        <!-- 权限选择 -->
        <el-form-item label="分享权限" prop="role">
          <el-select v-model="shareForm.role" placeholder="请选择">
            <el-option
              v-for="option in roleOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
          <div class="form-hint">
            • 管理员：完全控制权限<br/>
            • 操作员：可以控制机器人<br/>
            • 查看者：仅可查看状态
          </div>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="closeCreateShareModal">取消</el-button>
        <el-button type="primary" @click="handleCreateShare" :loading="creatingShare">
          确定
        </el-button>
      </div>
    </el-dialog>

    <!-- 编辑分享对话框 -->
    <el-dialog
      title="编辑分享"
      :visible.sync="editShareVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form :model="editForm" ref="editForm" label-width="100px" v-if="editingShare">
        <!-- 用户信息 -->
        <el-form-item label="分享给">
          <div class="user-info-display">
            <span class="display-name">{{ editingShare.to_user.username }}</span>
            <span v-if="editingShare.to_user.email" class="display-email">{{ editingShare.to_user.email }}</span>
          </div>
        </el-form-item>

        <!-- 权限选择 -->
        <el-form-item label="分享权限">
          <el-select v-model="editForm.role" placeholder="请选择">
            <el-option
              v-for="option in roleOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
          <div class="form-hint">
            • 管理员：完全控制权限<br/>
            • 操作员：可以控制机器人<br/>
            • 查看者：仅可查看状态
          </div>
        </el-form-item>

        <!-- 启用状态 -->
        <el-form-item label="启用状态">
          <el-switch
            v-model="editForm.is_active"
            active-text="启用"
            inactive-text="禁用"
          />
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="closeEditShareModal">取消</el-button>
        <el-button type="primary" @click="handleUpdateShare" :loading="updatingShare">
          保存
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getRobot } from "@/api/robot";
import { 
  searchUser, 
  createRobotShare, 
  getRobotShares, 
  updateRobotShare, 
  deleteRobotShare,
  toggleRobotShare 
} from "@/api/share";

export default {
  name: "RobotShare",
  data() {
    return {
      robotId: "",
      robot: null,
      loading: false,
      sharesLoading: false,
      searchingUser: false,
      creatingShare: false,
      updatingShare: false,
      shares: [],
      foundUser: null,
      editingShare: null,
      createShareVisible: false,
      editShareVisible: false,
      // 创建分享表单
      shareForm: {
        identifier: "",
        role: "operator",
      },
      shareFormRules: {
        identifier: [
          { required: true, message: '请输入用户名或邮箱', trigger: 'blur' }
        ],
        role: [
          { required: true, message: '请选择分享权限', trigger: 'change' }
        ]
      },
      // 编辑分享表单
      editForm: {
        role: "viewer",
        is_active: true,
      },
      roleOptions: [
        { value: "admin", label: "管理员" },
        { value: "operator", label: "操作员" },
        { value: "viewer", label: "查看者" },
      ],
    };
  },
  created() {
    this.robotId = this.$route.params.id;
    this.fetchRobot();
    this.fetchShares();
  },
  methods: {
    goBack() {
      this.$router.back();
    },

    // 获取机器人信息
    async fetchRobot() {
      this.loading = true;
      try {
        const response = await getRobot(this.robotId);
        this.robot = response.data.details;
      } finally {
        this.loading = false;
      }
    },

    // 获取分享列表
    async fetchShares() {
      this.sharesLoading = true;
      try {
        const response = await getRobotShares(this.robotId);
        this.shares = response.data.list || [];
      } finally {
        this.sharesLoading = false;
      }
    },

    // 打开创建分享弹窗
    openCreateShareModal() {
      this.shareForm = {
        identifier: "",
        role: "operator",
      };
      this.foundUser = null;
      this.createShareVisible = true;
      this.$nextTick(() => {
        if (this.$refs.shareForm) {
          this.$refs.shareForm.clearValidate();
        }
      });
    },

    // 关闭创建分享弹窗
    closeCreateShareModal() {
      this.createShareVisible = false;
      this.foundUser = null;
    },

    // 标识符输入改变
    onIdentifierChange() {
      this.foundUser = null;
    },

    // 搜索用户
    async handleSearchUser() {
      if (!this.shareForm.identifier || this.shareForm.identifier.trim() === "") {
        this.$message.warning('请输入用户名或邮箱');
        return;
      }

      this.searchingUser = true;
      try {
        const response = await searchUser(this.shareForm.identifier.trim());
        this.foundUser = response.data;
        this.$message.success('找到用户');
      } finally {
        this.searchingUser = false;
      }
    },

    // 创建分享
    async handleCreateShare() {
      if (!this.foundUser) {
        this.$message.warning('请先搜索并确认用户');
        return;
      }

      this.$refs.shareForm.validate(async (valid) => {
        if (!valid) return;

        this.creatingShare = true;
        try {
          await createRobotShare({
            robot_id: parseInt(this.robotId),
            to_user_id: this.foundUser.id,
            role: this.shareForm.role,
          });

          this.$message.success('创建成功');
          this.closeCreateShareModal();
          this.fetchShares();
        } finally {
          this.creatingShare = false;
        }
      });
    },

    // 打开编辑分享弹窗
    openEditShareModal(share) {
      this.editingShare = share;
      this.editForm = {
        role: share.role,
        is_active: share.is_active,
      };
      this.editShareVisible = true;
    },

    // 关闭编辑分享弹窗
    closeEditShareModal() {
      this.editShareVisible = false;
      this.editingShare = null;
    },

    // 更新分享
    async handleUpdateShare() {
      this.updatingShare = true;
      try {
        await updateRobotShare(this.editingShare.id, this.editForm);
        this.$message.success('保存成功');
        this.closeEditShareModal();
        this.fetchShares();
      } finally {
        this.updatingShare = false;
      }
    },

    // 切换分享状态
    async toggleShare(share) {
      await toggleRobotShare(share.id);
      this.$message.success(share.is_active ? '已禁用' : '已启用');
      this.fetchShares();
    },

    // 删除分享
    deleteShare(share) {
      this.$confirm(`确定要删除对用户"${share.to_user.username}"的分享吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await deleteRobotShare(share.id);
        this.$message.success('删除成功');
        this.fetchShares();
      }).catch(() => {});
    },

    // 获取设备类型标签
    getDeviceTypeLabel(type) {
      const typeMap = {
        single: "单开型",
        double: "双开型",
        compatible: "兼容型",
      };
      return typeMap[type] || type;
    },

    // 获取权限标签
    getRoleLabel(role) {
      const option = this.roleOptions.find(opt => opt.value === role);
      return option ? option.label : role;
    },

    // 获取权限颜色
    getRoleColor(role) {
      const colorMap = {
        admin: "danger",
        operator: "warning",
        viewer: "primary",
      };
      return colorMap[role] || "primary";
    },
  },
};
</script>

<style scoped>
.share-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  padding-bottom: 30px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.header h1 {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.back-btn {
  font-size: 16px;
}

.header-right {
  width: 80px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  gap: 16px;
}

.loading-text {
  color: #606266;
  font-size: 14px;
}

.main-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
}

/* 机器人信息卡片 */
.robot-info-card {
  margin-bottom: 20px;
}

.robot-info-card h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.robot-details {
  padding: 10px 0;
}

.detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.detail-label {
  color: #606266;
  width: 100px;
}

.detail-value {
  color: #303133;
  font-weight: 500;
}

/* 分享列表 */
.shares-section h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.shares-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: 40px;
}

.shares-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.share-item {
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #e4e7ed;
  cursor: pointer;
  transition: all 0.3s ease;
}

.share-item:hover {
  background-color: #ecf0f5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.share-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #dcdfe6;
}

.user-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.user-email {
  font-size: 12px;
  color: #909399;
}

.share-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.detail-row {
  display: flex;
  align-items: center;
  font-size: 14px;
  gap: 8px;
}

.detail-row .detail-label {
  color: #909399;
  min-width: 80px;
}

.detail-row .detail-value {
  color: #303133;
  flex: 1;
}

.share-actions {
  display: flex;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #dcdfe6;
}

.share-actions .el-button {
  flex: 1;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: 300px;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
  opacity: 0.5;
  color: #909399;
}

.empty-text {
  font-size: 16px;
  color: #909399;
  text-align: center;
  margin-bottom: 8px;
}

.empty-hint {
  font-size: 14px;
  color: #c0c4cc;
}

/* 对话框样式 */
.form-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  line-height: 1.6;
}

/* 找到的用户卡片 */
.found-user-card {
  background-color: #f0f9ff;
  border: 1px solid #b3d8ff;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
}

.found-user-header {
  margin-bottom: 10px;
}

.found-user-title {
  font-size: 14px;
  color: #409eff;
  font-weight: 600;
}

.found-user-info {
  display: flex;
  align-items: center;
}

.found-details {
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
}

.found-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.found-email {
  font-size: 12px;
  color: #909399;
}

/* 用户信息展示 */
.user-info-display {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 15px;
  background-color: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}

.display-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.display-email {
  font-size: 12px;
  color: #909399;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .main-content {
    padding: 0 15px;
  }

  .header {
    padding: 15px 20px;
  }

  .header h1 {
    font-size: 20px;
  }

  .share-item {
    padding: 12px;
  }

  .share-actions {
    flex-direction: column;
  }

  .share-actions .el-button {
    width: 100%;
  }
}
</style>
